import { isAbortError } from '@adv-frontend/error';
import { wait } from './wait';
import { hasMethod } from '../../utils';
import type { RequestMethod, RetryOptions } from '../../types';

export async function withClientRetry(
    request: () => Promise<Response>,
    method: RequestMethod,
    options: RetryOptions
): Promise<Response> {
    const { limit, calculateDelay } = options;

    if (!limit) {
        return request();
    }

    let attempt = 0;
    let response: Response | undefined;
    let error: unknown;

    while (attempt <= limit) {
        try {
            response = await request();
        } catch (catchedError) {
            error = catchedError;
        }

        if (!shouldRetry(options, method, response?.status, error)) {
            break;
        }

        attempt++;

        const delay = await calculateDelay({
            attemptCount: attempt,
            computedValue: getComputedValue(attempt),
            error,
            retryOptions: options,
        });

        if (delay === 0) {
            break;
        }

        await wait(delay);
    }

    if (!response) {
        throw error;
    }

    return response;
}

function shouldRetry(options: RetryOptions, method: RequestMethod, status: number | undefined, error: unknown) {
    const { methods, statusCodes } = options;

    if (isAbortError(error)) {
        return false;
    }

    if (!hasMethod(methods, method)) {
        return false;
    }

    return !status || statusCodes.includes(status);
}

function getComputedValue(attempt: number) {
    const noise = Math.random() * 100;

    return 2 ** (attempt - 1) * 1000 + noise;
}
